import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkles } from "lucide-react";
import { NAV_LINKS } from "@/data";
import { useApply } from "@/context";

const Logo = () => (
  <a href="#home" data-testid="logo-link" className="flex items-center gap-2.5 group">
    <span className="grid h-9 w-9 place-items-center rounded-xl bg-deep-trust-blue text-vibrant-orange transition-transform group-hover:rotate-6">
      <Sparkles size={18} strokeWidth={2.5} />
    </span>
    <span className="font-heading text-xl font-extrabold tracking-tight text-deep-trust-blue">
      Unnati<span className="text-emerald-green"> AI</span>
    </span>
  </a>
);

export default function Header() {
  const { openApply } = useApply();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "border-gray-200/60 bg-white/75 backdrop-blur-xl"
          : "border-transparent bg-white/40 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <Logo />

        <nav className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="relative text-sm font-medium text-deep-trust-blue/80 transition-colors hover:text-deep-trust-blue after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-vibrant-orange after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <button
            data-testid="apply-now-header-btn"
            onClick={() => openApply()}
            className="rounded-full bg-vibrant-orange px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-vibrant-orange/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-600 hover:shadow-vibrant-orange/40"
          >
            Apply Now
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((o) => !o)}
          className="grid h-10 w-10 place-items-center rounded-lg text-deep-trust-blue lg:hidden"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-gray-200/60 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg px-3 py-3 text-base font-medium text-deep-trust-blue transition-colors hover:bg-light-gray"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="apply-now-mobile-btn"
                onClick={() => {
                  setMobileOpen(false);
                  openApply();
                }}
                className="mt-2 rounded-full bg-vibrant-orange px-6 py-3 text-base font-semibold text-white"
              >
                Apply Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
